// Saha Yönetimi - Field Activity Types

import { ApprovalFlowStep } from './approval.types';
import { VehicleAssignmentType } from './vehicle.types';

export enum ActivityType {
  VISIT = 'VISIT',
  DELIVERY = 'DELIVERY',
  MAINTENANCE = 'MAINTENANCE',
  OFFICE = 'OFFICE',
  OTHER = 'OTHER'
}

export enum ActivityStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  PLANNED = 'PLANNED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED'
}

/** Onay sürecindeki (henüz planlanmamış) aktivite durumları: tamamlanamaz, sadece iptal edilebilir */
export const APPROVAL_LOCKED_ACTIVITY_STATUSES: ActivityStatus[] = [
  ActivityStatus.PENDING_APPROVAL,
  ActivityStatus.REJECTED
];

export interface FieldActivityExpense {
  id: number;
  fieldActivityId?: number;
  amount: number | string;
  currency: string;
  description?: string;
  expenseDate: string;
  receiptPath?: string;
  receiptUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: { id: number; firstName: string; lastName: string; email?: string };
}

export interface FieldActivityAttachment {
  id: number;
  fieldActivityId: number;
  filePath: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: string;
}

export interface FieldActivity {
  id: number;
  companyId: number;
  title: string;
  type: ActivityType;
  description?: string;
  startDate: string;
  endDate: string;
  completedAt: string | null;
  status: ActivityStatus;
  isBillable: boolean;
  completionNotes?: string;
  createdById: number;
  createdAt: string;
  updatedAt: string;
  vehicleId?: number;
  vehicle?: {
    id: number;
    name: string;
    plateNumber: string;
    assignmentType?: VehicleAssignmentType;
  };
  rejectionReason?: string | null;
  reviewedAt?: string | null;
  approvedBy?: { id: number; firstName: string; lastName: string } | null;
  approvalFlow?: ApprovalFlowStep[];
  assignedTo?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  }> | {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedUsers?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  }>;
  expenses?: FieldActivityExpense[];
  attachments?: FieldActivityAttachment[];
  totalExpenses?: number;
}

export interface CreateFieldActivityDTO {
  title: string;
  type: ActivityType;
  description?: string;
  startDate: string;
  endDate: string;
  status?: ActivityStatus;
  completionNotes?: string;
  completedAt?: string | null;
  isBillable: boolean;
  userIds: number[];  // Backend array olarak bekliyorsa array gönder, tek eleman olacak
  vehicleId?: number;
}

export interface UpdateFieldActivityDTO {
  title?: string;
  type?: ActivityType;
  description?: string;
  startDate?: string;
  endDate?: string;
  status?: ActivityStatus;
  isBillable?: boolean;
  completionNotes?: string;
  completedAt?: string | null;
}

/** GET /field-activities/pending-approvals liste elemanı */
export interface PendingFieldActivity {
  id: number;
  title: string;
  type: ActivityType;
  description?: string;
  startDate: string;
  endDate?: string | null;
  status: ActivityStatus;
  assignedTo: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    profilePicture?: string | null;
    primaryDepartment?: { id: number; name: string };
  };
  createdBy?: { id: number; firstName: string; lastName: string };
  vehicle?: {
    id: number;
    name: string;
    plateNumber: string;
    assignmentType?: VehicleAssignmentType;
  } | null;
  approvalFlow: ApprovalFlowStep[];
}

export interface ReviewFieldActivityDTO {
  action: 'approve' | 'reject';
  rejectionReason?: string;
}

export interface AvailableVehiclesParams {
  userId?: number;
  startDate: string;
  endDate?: string;
  excludeActivityId?: number;
}

export interface CreateExpenseDTO {
  amount: number;
  currency: string;
  description?: string;
  expenseDate: string;
  receipt?: File;
}

export interface UpdateExpenseDTO {
  amount?: number;
  currency?: string;
  description?: string;
  expenseDate?: string;
  receipt?: File;
}

export interface FieldActivityFilters {
  assignedToIds?: string;
  status?: ActivityStatus;
  startDate?: string;
  endDate?: string;
  type?: ActivityType;
}

export interface LocationLog {
  id: number;
  latitude: string;
  longitude: string;
  timestamp: string;
}

export interface ActivityLocation extends LocationLog {
  // Aktiviteye ait konum
}

export interface ActivityWithLocations {
  id: number;
  title: string;
  type: ActivityType;
  startDate: string;
  endDate: string;
  status: ActivityStatus;
  locations: ActivityLocation[];
}

export interface RouteResponse {
  route: LocationLog[];
  activities: ActivityWithLocations[];
}

export interface LocationRouteResponse {
  code: number;
  message: string;
  data: RouteResponse;
}
